import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BLOG_POSTS } from "../blogData";
import bHero from "../assets/blog/blog_hero_banner.png";

// Inline SVGs for metadata
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="3" />
  </svg>
);

const CommentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const ViewsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Social share icons
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
  </svg>
);

const TwitterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
  </svg>
);

const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const BlogDetailPage = () => {
  const { slug } = useParams();
  
  // Find current post index and post data
  const postIndex = BLOG_POSTS.findIndex(p => p.slug === slug);
  const post = BLOG_POSTS[postIndex];

  // Reply Form states
  const [commentName, setCommentName] = useState("");
  const [commentEmail, setCommentEmail] = useState("");
  const [commentMsg, setCommentMsg] = useState("");
  const [submitStatus, setSubmitStatus] = useState("");
  const [commentsList, setCommentsList] = useState([]);

  // Sidebar states
  const [searchQuery, setSearchQuery] = useState("");

  // Scroll to top when slug/post changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (post) {
      setCommentsList(post.comments || []);
      setSubmitStatus("");
    }
  }, [slug, post]);

  if (!post) {
    return (
      <div className="rest-page">
        <Header />
        <section className="rest-blog-banner" style={{ backgroundImage: `url(${bHero})` }}>
          <div className="max-w-7xl mx-auto">
            <h1>Article Not Found</h1>
            <div className="rest-blog-breadcrumbs">
              <Link to="/">Home</Link>
              <span>/</span>
              <Link to="/blog">Blog</Link>
            </div>
          </div>
        </section>
        <main className="rest-blog-container text-center py-20">
          <h2 className="text-2xl font-bold text-neutral-800 mb-4">Post not found</h2>
          <p className="text-neutral-500 mb-8">The blog article you are looking for does not exist or has been removed.</p>
          <Link to="/blog" className="rest-reply-submit-btn">Back to Blog</Link>
        </main>
        <Footer />
      </div>
    );
  }

  // Prev / Next posts
  const prevPost = postIndex > 0 ? BLOG_POSTS[postIndex - 1] : null;
  const nextPost = postIndex < BLOG_POSTS.length - 1 ? BLOG_POSTS[postIndex + 1] : null;

  // Related posts (same category, excluding current post)
  const relatedPosts = BLOG_POSTS.filter(p => p.category === post.category && p.slug !== post.slug).slice(0, 2);
  
  // If not enough related posts, grab from same tags or just general posts
  const fallbackRelated = relatedPosts.length < 2 
    ? [...relatedPosts, ...BLOG_POSTS.filter(p => p.slug !== post.slug && !relatedPosts.some(r => r.slug === p.slug))].slice(0, 2)
    : relatedPosts;

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!commentName || !commentEmail || !commentMsg) {
      setSubmitStatus("error");
      return;
    }
    
    // Add new comment
    const newComment = {
      id: commentsList.length + 1,
      author: commentName,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      text: commentMsg
    };

    setCommentsList([...commentsList, newComment]);
    setCommentName("");
    setCommentEmail("");
    setCommentMsg("");
    setSubmitStatus("success");
  };

  return (
    <div className="rest-page">
      <Header />

      {/* Blog Details Banner */}
      <section 
        className="rest-blog-banner" 
        style={{ backgroundImage: `url(${bHero})` }}
      >
        <div className="max-w-7xl mx-auto">
          <h1>{post.category}</h1>
          <div className="rest-blog-breadcrumbs">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blog</Link>
            <span>/</span>
            <span>{post.title}</span>
          </div>
        </div>
      </section>

      {/* Main Container */}
      <main className="rest-blog-container">
        <div className="rest-blog-layout">
          
          {/* Details Column */}
          <div className="rest-blog-main-content">
            <article className="rest-blog-detail">
              
              {/* Featured Image */}
              <img 
                src={post.imgUrl} 
                alt={post.title} 
                className="rest-detail-featured-img"
              />

              {/* Metadata */}
              <div className="rest-detail-meta">
                <span>
                  <UserIcon />
                  By {post.author}
                </span>
                <span>
                  <TagIcon />
                  {post.tag}
                </span>
                <span>
                  <CommentIcon />
                  {commentsList.length} Comments
                </span>
                <span>
                  <ViewsIcon />
                  {post.views} Views
                </span>
              </div>

              {/* Title */}
              <h2 className="rest-detail-title">{post.title}</h2>

              {/* Body Content */}
              <div className="rest-post-body">
                {post.content.map((block, idx) => {
                  if (block.type === "paragraph") {
                    return <p key={idx}>{block.text}</p>;
                  }
                  if (block.type === "blockquote") {
                    return (
                      <blockquote key={idx}>
                        <p>{block.text}</p>
                      </blockquote>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Tags & Social Shares Bar */}
              <div className="rest-post-share-wrap">
                <div className="rest-post-tags">
                  <span className="rest-post-tags-title">Tags:</span>
                  <Link to="/blog" className="rest-tag-pill active">{post.tag}</Link>
                  <Link to="/blog" className="rest-tag-pill">{post.category}</Link>
                </div>
                
                <div className="rest-post-shares">
                  <span className="rest-post-shares-title">Share:</span>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="rest-share-social-btn" aria-label="Facebook">
                    <FacebookIcon />
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="rest-share-social-btn" aria-label="Twitter">
                    <TwitterIcon />
                  </a>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="rest-share-social-btn" aria-label="LinkedIn">
                    <LinkedinIcon />
                  </a>
                </div>
              </div>

              {/* Post Navigation (Prev / Next box) */}
              <div className="rest-post-navigation">
                {prevPost ? (
                  <Link to={`/blog/${prevPost.slug}`} className="rest-post-nav-box prev">
                    <span className="rest-post-nav-label">
                      &larr; Previous Post
                    </span>
                    <h4 className="rest-post-nav-title">{prevPost.title}</h4>
                  </Link>
                ) : (
                  <div className="rest-post-nav-box prev" style={{ opacity: 0.3 }}>
                    <span className="rest-post-nav-label">First Post</span>
                    <h4 className="rest-post-nav-title">No older articles</h4>
                  </div>
                )}

                {nextPost ? (
                  <Link to={`/blog/${nextPost.slug}`} className="rest-post-nav-box next">
                    <span className="rest-post-nav-label">
                      Next Post &rarr;
                    </span>
                    <h4 className="rest-post-nav-title">{nextPost.title}</h4>
                  </Link>
                ) : (
                  <div className="rest-post-nav-box next" style={{ opacity: 0.3 }}>
                    <span className="rest-post-nav-label">Latest Post</span>
                    <h4 className="rest-post-nav-title">No newer articles</h4>
                  </div>
                )}
              </div>

              {/* Related Posts */}
              <div className="rest-related-posts-wrap">
                <h3 className="rest-related-title">Related Articles</h3>
                <div className="rest-related-grid">
                  {fallbackRelated.map((rel) => (
                    <div key={rel.id} className="rest-related-card">
                      <img src={rel.imgUrl} alt={rel.title} className="rest-related-card-img" />
                      <div className="rest-related-card-body">
                        <span className="rest-related-card-date">{rel.day} {rel.month} {rel.year}</span>
                        <h4 className="rest-related-card-title">
                          <Link to={`/blog/${rel.slug}`}>{rel.title}</Link>
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comments list display */}
              <div className="rest-comments-wrap">
                <h3 className="rest-comments-title">Comments ({commentsList.length})</h3>
                {commentsList.length > 0 ? (
                  <div className="rest-comment-list">
                    {commentsList.map((comm) => (
                      <div key={comm.id} className="rest-comment-item">
                        <div className="rest-comment-avatar">
                          {comm.author.charAt(0).toUpperCase()}
                        </div>
                        <div className="rest-comment-content">
                          <div className="rest-comment-meta">
                            <span className="rest-comment-author">{comm.author}</span>
                            <span className="rest-comment-date">{comm.date}</span>
                          </div>
                          <p className="rest-comment-text">{comm.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-neutral-500 italic">No comments yet. Be the first to comment!</p>
                )}
              </div>

              {/* Leave a reply form */}
              <div className="rest-reply-form-wrap">
                <h3 className="rest-reply-title">Leave a Reply</h3>
                <p className="rest-reply-subtitle">Your email address will not be published. Required fields are marked *</p>
                
                {submitStatus === "success" && (
                  <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-4 py-3 rounded-md mb-6 text-sm">
                    Thank you! Your comment has been posted successfully below.
                  </div>
                )}
                {submitStatus === "error" && (
                  <div className="bg-rose-50 text-rose-800 border border-rose-200 px-4 py-3 rounded-md mb-6 text-sm">
                    Please fill out all fields before submitting.
                  </div>
                )}

                <form onSubmit={handleCommentSubmit}>
                  <div className="rest-form-grid">
                    <input 
                      type="text" 
                      placeholder="Name *" 
                      className="rest-form-input"
                      value={commentName}
                      onChange={(e) => setCommentName(e.target.value)}
                      required 
                    />
                    <input 
                      type="email" 
                      placeholder="Email *" 
                      className="rest-form-input"
                      value={commentEmail}
                      onChange={(e) => setCommentEmail(e.target.value)}
                      required 
                    />
                    <textarea 
                      rows="6" 
                      placeholder="Write your comment here... *" 
                      className="rest-form-textarea"
                      value={commentMsg}
                      onChange={(e) => setCommentMsg(e.target.value)}
                      required
                    ></textarea>
                  </div>
                  <button type="submit" className="rest-reply-submit-btn">
                    Post Comment
                  </button>
                </form>
              </div>

            </article>
          </div>

          {/* Sidebar Column */}
          <aside className="rest-blog-sidebar">
            
            {/* Search Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Search</h3>
              <div className="rest-search-form">
                <input 
                  type="text" 
                  className="rest-search-input" 
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      window.location.href = `/blog?search=${searchQuery}`;
                    }
                  }}
                />
                <button 
                  onClick={() => window.location.href = `/blog?search=${searchQuery}`} 
                  className="rest-search-submit" 
                  aria-label="Search"
                >
                  <SearchIcon />
                </button>
              </div>
            </div>

            {/* Categories Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Categories</h3>
              <ul className="rest-category-list">
                {["Fast Food", "Health & Safety", "Food Stories", "Burgers", "Cooking Tips"].map((cat) => (
                  <li 
                    key={cat}
                    className="rest-category-item"
                    onClick={() => window.location.href = `/blog`}
                  >
                    <span>{cat}</span>
                    <span className="rest-category-count">
                      {BLOG_POSTS.filter(p => p.category === cat).length}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Recent Posts Widget */}
            <div className="rest-sidebar-widget">
              <h3 className="rest-widget-title">Recent Posts</h3>
              <div className="rest-recent-post-list">
                {BLOG_POSTS.slice(0, 3).map((post) => (
                  <div key={post.id} className="rest-recent-post-item">
                    <img 
                      src={post.imgUrl} 
                      alt={post.title} 
                      className="rest-recent-post-img"
                    />
                    <div className="rest-recent-post-info">
                      <span className="rest-recent-post-date">{post.day} {post.month} {post.year}</span>
                      <h4 className="rest-recent-post-title">
                        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                      </h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </aside>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
